<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.code"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('customer.profession')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.profession"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('customer.defaultPaymentType')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownPaymentTypes"
            :selected-item="selectedPaymentType"
            name="paymentTypes"
            flat
            :mode="mode"
            @update:selected="handlePaymentTypeSelection"
            clearable
            show-error-as-tooltip
            :error="
              validations?.defaultPaymentType.required.$invalid
                ? validations.defaultPaymentType.required.$message
                : ''
            " />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('customer.defaultDocumentType')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownDocumentTypes"
            :selected-item="selectedDocumentType"
            name="documentTypes"
            flat
            :mode="mode"
            @update:selected="handleDocumentTypeSelection"
            clearable />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('customer.paymentDays')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-model="data.paymentDays"
            :mode="mode"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="
              validations?.paymentDays.integer.$invalid
                ? validations.paymentDays.integer.$message
                : ''
            " />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('customer.discountPercentage')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.discountpercentage"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="
              validations?.discountPercentage.minValue.$invalid
                ? validations.discountPercentage.minValue.$message
                : validations?.discountPercentage.maxValue.$invalid
                  ? validations.discountPercentage.maxValue.$message
                  : ''
            " />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('customer.plafond')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.plafond"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('customer.minOrder')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.minOrder"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle v-else label="Active" name="isActive" v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.lastUpdate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.updateUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.updateUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.createdDate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createUser?.name || '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { isoDateToFullDate } from '@/utils/date';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import { type Customer } from '@/types/Traders';
import type { SelectDropdownItem } from '@/types/Base';
import type { Validation } from '@vuelidate/core';

const data = defineModel<Customer>();
defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  paymentTypeErrorMessage?: string;
  validations?: Validation;
}>();

const { dropdownPaymentTypes, dropdownDocumentTypes } = storeToRefs(useLookupsStore());
const { getPaymentTypesLookup, getDocumentTypesLookup } = useLookupsStore();
const selectedPaymentType = ref<SelectDropdownItem | null>(null);
const selectedDocumentType = ref<SelectDropdownItem | null>(null);

function handlePaymentTypeSelection(item: SelectDropdownItem) {
  selectedPaymentType.value = item ? item : null;
  if (data.value) data.value.defaultPaymentTypeId = item ? (item.value as string) : undefined;
}

function handleDocumentTypeSelection(item: SelectDropdownItem) {
  selectedDocumentType.value = item ? item : null;
  if (data.value) data.value.defaultDocumentTypeId = item ? (item.value as string) : undefined;
}

function initDropdowns() {
  if (!data.value) return;

  if (data.value.defaultPaymentTypeId) {
    const selectedValue = dropdownPaymentTypes.value.find(
      (item) => item.value == data.value?.defaultPaymentTypeId
    );
    if (selectedValue) selectedPaymentType.value = selectedValue;
  }

  if (data.value.defaultDocumentTypeId) {
    const selectedValue = dropdownDocumentTypes.value.find(
      (item) => item.value === data.value?.defaultDocumentTypeId
    );
    if (selectedValue) selectedDocumentType.value = selectedValue;
  }
}

onMounted(async () => {
  await getPaymentTypesLookup();
  await getDocumentTypesLookup();
  initDropdowns();
});

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);
</script>
